import React, { useState, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { upscaleImage } from '../services/upscaleService';
import { useToast } from './Toast';
import { Watermark, addWatermarkToImage, generatedImageDownloadFilename } from './Watermark';
import { RunningHubCosExpiryBadge } from './RunningHubCosExpiryBadge';
import { RUNNINGHUB_OUTPUT_MAX_AGE_MS, RUNNINGHUB_LOCAL_HISTORY_MAX_ITEMS } from '../utils/runningHubRetention';
import {
  createScopedRunningHubClientItemId,
  syncRunningHubHistoryItemToCloud,
  deleteRunningHubHistoryFromCloud,
} from '../utils/runningHubCloudHistory';
import { isRunningHubListThumbUrl } from '../utils/runningHubHistoryLocalStorage';
import {
  writeRunningHubHistoryListToLocalStorage,
  stampRunningHubHistoryOwner,
} from '../utils/runningHubHistoryScope';
import { RunningHubHistoryListThumb } from './RunningHubHistoryListThumb';
import { useRunningHubLinkIndicator } from '../utils/useRunningHubLinkIndicator';
import { useCreditDeduction } from '../hooks/useCreditDeduction';
import { useRunningHubPanelHistory } from '../hooks/useRunningHubPanelHistory';
import type { Lang } from '../utils/i18n';
import {
  startCompareSliderPointerDrag,
  startCompareSliderTouchDrag,
} from '../utils/compareSliderPointer';
import { attachComparePanelPinchZoom } from '../utils/comparePanelPinchZoom';
import { createRunningHubClientItemId, type RunningHubRecoveryRequest } from '../utils/applyRunningHubJobRecovery';
import { useRunningHubPanelRecovery } from '../hooks/useRunningHubPanelRecovery';
import {
  getUnsupportedUploadImageFormatMessage,
  isSupportedUploadImageDataUrl,
  isSupportedUploadImageFile,
} from '../utils/supportedUploadImageFormat';

interface UpscalePanelProps {
  isOpen: boolean;
  onClose: () => void;
  sourceImage: string | null;
  isDarkMode: boolean;
  language: Lang;
  userEmail?: string;
  /** 登录后把侧栏历史同步到 Supabase（仅存 JSON / URL，图片仍在 COS） */
  isAuthenticated?: boolean;
  recoveryRequest?: RunningHubRecoveryRequest | null;
}

const UpscalePanel: React.FC<UpscalePanelProps> = ({ 
  isOpen, 
  onClose, 
  sourceImage, 
  isDarkMode, 
  language,
  userEmail,
  isAuthenticated = false,
  recoveryRequest = null,
}) => {
  const { showToast } = useToast();
  
  // 积分扣费 Hook
  const { checkBalance } = useCreditDeduction({
    language: language === 'en' ? 'en' : 'zh',
    onInsufficientBalance: (required, available) => {
      showToast(
        language === 'en' 
          ? `Insufficient credits: need ${required}, have ${available}. Please recharge.` 
          : `积分不足：需要 ${required} 积分，当前 ${available} 积分，请及时充值`,
        'warning'
      );
    },
    onDeductionError: (error) => {
      showToast(
        language === 'en' ? `Credit deduction failed: ${error}` : `扣费失败：${error}`,
        'error'
      );
    }
  });
  
  const [scale, setScale] = useState(2); // 2=4K, 3=6K, 4=8K
  const [isProcessing, setIsProcessing] = useState(false);
  /** 与请求生命周期绑定：关面板不得清空，防止未返回结果时可再次点击生成 */
  const generationInFlightRef = useRef(false);
  const [progressDesc, setProgressDesc] = useState("");
  const [progressPercent, setProgressPercent] = useState(0); // 进度百分比
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [currentSourceImage, setCurrentSourceImage] = useState<string | null>(null); // 当前显示的原始图
  const [sliderPosition, setSliderPosition] = useState(50); // 滑杆位置 0-100
  const [isDragging, setIsDragging] = useState(false); // 是否正在拖动滑杆
  const [zoomLevel, setZoomLevel] = useState(1); // 缩放级别
  const [panX, setPanX] = useState(0); // 水平平移
  const [panY, setPanY] = useState(0); // 垂直平移
  const [isPanning, setIsPanning] = useState(false); // 是否正在平移
  const [panStart, setPanStart] = useState({ x: 0, y: 0 }); // 平移起始位置
  const [upscaleHistory, setUpscaleHistory] = useState<
    Array<{ id: string; originalImage: string; upscaledUrl: string; scale: number; timestamp: number; thumbUrl?: string }>
  >([]);
  const [imageBounds, setImageBounds] = useState<{left: number, width: number} | null>(null); // 图片实际显示区域
  const [selectedHistoryId, setSelectedHistoryId] = useState<string | null>(null); // 当前选中的历史记录项ID
  const runningHubLinked = useRunningHubLinkIndicator(isOpen);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const originalImageRef = useRef<HTMLImageElement>(null);
  const upscaledImageRef = useRef<HTMLImageElement>(null);
  const imageWrapperRef = useRef<HTMLDivElement>(null); // 图片包装容器引用
  const zoomLevelPinchRef = useRef(1);
  const sourceFileInputRef = useRef<HTMLInputElement>(null);
  const [sourceDropOver, setSourceDropOver] = useState(false);

  zoomLevelPinchRef.current = zoomLevel;

  const t = (en: string, zh: string) => language === 'en' ? en : zh;

  /** 侧栏预览与放大 API 统一使用：本地拖入/选择优先，否则用主界面传入图 */
  const effectiveSourceImage = useMemo(() => currentSourceImage ?? sourceImage, [currentSourceImage, sourceImage]);

  // 获取分辨率标签
  const getResolutionLabel = (value: number) => {
    if (value === 2) return '2';
    if (value === 3) return '3';
    if (value === 4) return '4';
    return `${value}`;
  };

  const readFileAsDataUrl = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(typeof r.result === 'string' ? r.result : '');
      r.onerror = () => reject(r.error ?? new Error('read failed'));
      r.readAsDataURL(file);
    });

  const applyLocalSourceFile = async (file: File) => {
    if (!isSupportedUploadImageFile(file)) {
      showToast(getUnsupportedUploadImageFormatMessage(language === 'en' ? 'en' : 'zh'), 'warning');
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      setCurrentSourceImage(dataUrl || null);
      setResultUrl(null);
      setSelectedHistoryId(null);
      setSliderPosition(50);
      setZoomLevel(1);
      setPanX(0);
      setPanY(0);
    } catch {
      showToast(t('Failed to read image', '读取图片失败'), 'error');
    }
  };

  // 处理放大
  const handleUpscale = async () => {
    if (generationInFlightRef.current) return;
    if (!effectiveSourceImage) {
      showToast(t('No image available', '没有可用的图片'), 'warning');
      return;
    }
    if (!isSupportedUploadImageDataUrl(effectiveSourceImage)) {
      showToast(getUnsupportedUploadImageFormatMessage(language === 'en' ? 'en' : 'zh'), 'warning');
      return;
    }

    generationInFlightRef.current = true;
    try {
      // 【积分】仅预检：实际扣费在 /api/upscale（避免前端 deduct + 后端再扣导致双倍）
      const { sufficient, required, available } = await checkBalance('upscale');
      if (!sufficient) {
        showToast(
          language === 'en'
            ? `Insufficient credits: need ${required}, have ${available}. Please recharge.`
            : `积分不足：需要 ${required} 积分，当前 ${available} 积分，请及时充值`,
          'warning',
        );
        return;
      }

      setIsProcessing(true);
      setProgressDesc(t('Submitting upscale task...', '正在提交放大任务...'));
      setProgressPercent(10);
      setResultUrl(null);

      let progressInterval: ReturnType<typeof setInterval> | undefined;
      try {
        progressInterval = setInterval(() => {
          setProgressPercent((prev) => {
            if (prev >= 90) return prev;
            return prev + Math.random() * 5;
          });
        }, 1000);

        const clientItemId =
          isAuthenticated && userEmail
            ? createRunningHubClientItemId('upscale', userEmail, true)
            : '';
        const jobMeta =
          clientItemId ?
            {
              clientItemId,
              historyPayloadDraft: {
                id: clientItemId,
                originalImage: effectiveSourceImage,
                scale,
                timestamp: Date.now(),
              },
            }
          : undefined;

        const result = await upscaleImage(
          effectiveSourceImage,
          scale,
          (desc) => {
          setProgressDesc(desc);
          if (desc.includes('完成') || desc.includes('completed')) {
            setProgressPercent(100);
          } else if (desc.includes('提交') || desc.includes('Submitting')) {
            setProgressPercent(20);
          } else if (desc.includes('处理') || desc.includes('Processing')) {
            setProgressPercent(50);
          }
        },
          userEmail,
          jobMeta,
        );

        setProgressPercent(100);
        setResultUrl(result.url);
        setProgressDesc(t('Upscale completed!', '放大完成！'));

        saveToHistory(effectiveSourceImage, result.url, scale, {
          id: clientItemId || undefined,
          skipCloudSync: !!result.historyArchived,
        });
      } catch (error: any) {
        setProgressDesc(t('Upscale failed', '放大失败'));
        setProgressPercent(0);
        if (error?.status === 403 || error?.isInsufficientBalance) {
          alert(language === 'zh' ? '积分不足或已过期，请及时充值' : 'Insufficient credits or expired. Please recharge.');
        } else {
          showToast(t(`Upscale Failed: ${error.message}`, `放大失败: ${error.message}`), 'error');
        }
      } finally {
        if (progressInterval) clearInterval(progressInterval);
      }
    } finally {
      generationInFlightRef.current = false;
      setIsProcessing(false);
    }
  };

  // 下载图片
  const handleDownload = async () => {
    if (!resultUrl) {
      showToast(t('No image to download', '没有可下载的图片'), 'warning');
      return;
    }

    try {
      // 添加水印到图片
      showToast(t('Adding watermark...', '正在添加水印...'), 'info');
      const watermarkedImage = await addWatermarkToImage(resultUrl);
      
      // 创建下载链接
      const link = document.createElement('a');
      link.href = watermarkedImage;
      link.download = generatedImageDownloadFilename(Date.now());
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // 延迟清理
      setTimeout(() => {
        document.body.removeChild(link);
      }, 100);
      
      showToast(t('Download started', '下载已开始'), 'success');
    } catch (error: any) {
      console.error('Download failed:', error);
      showToast(
        t(
          'Download failed. Sign in and retry if the image is from cloud storage.',
          '下载失败，请登录账号后重试（云端图片需后端拉取才能加水印）。',
        ),
        'error',
      );
    }
  };

  const cleanExpiredUpscaleHistory = (
    items: Array<{ id: string; originalImage: string; upscaledUrl: string; scale: number; timestamp: number }>,
  ): Array<{ id: string; originalImage: string; upscaledUrl: string; scale: number; timestamp: number }> => {
    const now = Date.now();
    const filtered = items.filter((item) => now - item.timestamp < RUNNINGHUB_OUTPUT_MAX_AGE_MS);
    if (filtered.length !== items.length) {
      console.log(
        `🧹 清理过期放大历史记录: ${items.length} -> ${filtered.length} (删除了 ${items.length - filtered.length} 条超过 RunningHub 14 天保留期的记录)`,
      );
    }
    return filtered;
  };

  useRunningHubPanelHistory({
    featureType: 'upscale',
    isOpen,
    isAuthenticated: !!isAuthenticated,
    userEmail,
    language,
    showToast,
    setHistory: setUpscaleHistory,
    cleanExpired: cleanExpiredUpscaleHistory,
    onBeforeClear: () => setSelectedHistoryId(null),
  });

  // 保存历史记录到 localStorage（保存前先清理过期记录）
  const saveToHistory = (
    originalImage: string,
    upscaledUrl: string,
    scaleVal: number,
    opts?: { id?: string; skipCloudSync?: boolean; thumbUrl?: string },
  ) => {
    const newItem = stampRunningHubHistoryOwner(
      {
        id:
          opts?.id ||
          createScopedRunningHubClientItemId('upscale', userEmail, !!isAuthenticated),
        originalImage,
        upscaledUrl,
        scale: scaleVal,
        timestamp: Date.now(),
        ...(opts?.thumbUrl && isRunningHubListThumbUrl(opts.thumbUrl) ? { thumbUrl: opts.thumbUrl } : {}),
      },
      userEmail,
    );
    if (!newItem) return;
    setUpscaleHistory((prev) => {
      const cleaned = cleanExpiredUpscaleHistory(prev);
      const updated = [newItem, ...cleaned.filter((x) => x.id !== newItem.id)].slice(
        0,
        RUNNINGHUB_LOCAL_HISTORY_MAX_ITEMS,
      );
      writeRunningHubHistoryListToLocalStorage('upscale', userEmail, updated as unknown as Record<string, unknown>[]);
      if (!opts?.skipCloudSync) {
        void (async () => {
          const r = await syncRunningHubHistoryItemToCloud('upscale', !!isAuthenticated, newItem.id, newItem, {
            userEmailForLog: userEmail,
            currentUserEmail: userEmail,
          });
          if (!r.ok && !r.skipped) {
            showToast(
              language === 'en' ? `Cloud archive failed: ${r.error || ''}` : `云端存档失败：${r.error || ''}`,
              'error',
            );
          }
          if (r.thumbUrl && isRunningHubListThumbUrl(r.thumbUrl)) {
            setUpscaleHistory((cur) => {
              const mapped = cur.map((x) => (x.id === newItem.id ? { ...x, thumbUrl: r.thumbUrl } : x));
              writeRunningHubHistoryListToLocalStorage('upscale', userEmail, mapped as unknown as Record<string, unknown>[]);
              return mapped;
            });
          }
        })();
      }
      return updated;
    });
  };

  const handleSelectHistoryItem = (item: (typeof upscaleHistory)[0]) => {
    // 设置选中状态
    setSelectedHistoryId(item.id);
    // 设置放大后的图片
    setResultUrl(item.upscaledUrl);
    // 设置原始图（使用历史记录中保存的原始图）
    setCurrentSourceImage(item.originalImage);
    // 设置放大倍率
    setScale(item.scale);
    // 重置滑杆和缩放
    setSliderPosition(50);
    setZoomLevel(1);
    setPanX(0);
    setPanY(0);
  };

  useRunningHubPanelRecovery({
    featureType: 'upscale',
    recoveryRequest,
    mapPayloadToItem: (p) => {
      const upscaledUrl = typeof p.upscaledUrl === 'string' ? p.upscaledUrl : '';
      if (!upscaledUrl) return null;
      return {
        id: String(
          p.id || createScopedRunningHubClientItemId('upscale', userEmail, !!isAuthenticated),
        ),
        originalImage: String(p.originalImage || ''),
        upscaledUrl,
        scale: Number(p.scale || 2),
        timestamp: Number(p.timestamp || Date.now()),
        thumbUrl:
          typeof p.thumbUrl === 'string' && isRunningHubListThumbUrl(p.thumbUrl) ? p.thumbUrl : undefined,
      };
    },
    mergeHistory: (item) => {
      saveToHistory(item.originalImage, item.upscaledUrl, item.scale, {
        id: item.id,
        skipCloudSync: true,
        thumbUrl: item.thumbUrl,
      });
    },
    applyItem: handleSelectHistoryItem,
  });

  // 下载历史记录中的图片
  const handleDownloadHistoryItem = async (e: React.MouseEvent, item: typeof upscaleHistory[0]) => {
    e.stopPropagation(); // 阻止触发选择历史记录项
    try {
      // 添加水印到图片
      showToast(t('Adding watermark...', '正在添加水印...'), 'info');
      const watermarkedImage = await addWatermarkToImage(item.upscaledUrl);
      
      // 创建下载链接
      const link = document.createElement('a');
      link.href = watermarkedImage;
      link.download = generatedImageDownloadFilename(item.timestamp);
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
      }, 100);
      
      showToast(t('Download started', '下载已开始'), 'success');
    } catch (error: any) {
      console.error('Download failed:', error);
      showToast(
        t(
          'Download failed. Sign in and retry if the image is from cloud storage.',
          '下载失败，请登录账号后重试（云端图片需后端拉取才能加水印）。',
        ),
        'error',
      );
    }
  };

  // 删除历史记录项
  const handleDeleteHistoryItem = (e: React.MouseEvent, itemId: string) => {
    e.stopPropagation(); // 阻止触发选择历史记录项
    if (confirm(t('Delete this upscale history?', '确定要删除这条放大历史吗？'))) {
      void deleteRunningHubHistoryFromCloud('upscale', !!isAuthenticated, itemId);
      const updated = upscaleHistory.filter(item => item.id !== itemId);
      setUpscaleHistory(updated);
      writeRunningHubHistoryListToLocalStorage('upscale', userEmail, updated as unknown as Record<string, unknown>[]);
    }
  };

  // 主界面渲染/命令会改 project.renderedImage；高清放大请求进行中不同步，避免滑杆对比底图被其它任务刷新。
  useEffect(() => {
    if (generationInFlightRef.current) return;
    setCurrentSourceImage(sourceImage ?? null);
  }, [sourceImage, isProcessing]);

  // 更新图片边界（用于滑杆定位）
  useEffect(() => {
    const updateImageBounds = () => {
      if (originalImageRef.current && imageContainerRef.current) {
        const imageRect = originalImageRef.current.getBoundingClientRect();
        const containerRect = imageContainerRef.current.getBoundingClientRect();
        setImageBounds({
          left: imageRect.left - containerRect.left,
          width: imageRect.width
        });
      }
    };

    // 初始更新
    updateImageBounds();

    // 监听窗口大小变化
    window.addEventListener('resize', updateImageBounds);
    
    // 使用 MutationObserver 监听图片尺寸变化
    const observer = new MutationObserver(updateImageBounds);
    if (originalImageRef.current) {
      observer.observe(originalImageRef.current, {
        attributes: true,
        attributeFilter: ['style', 'width', 'height']
      });
    }

    return () => {
      window.removeEventListener('resize', updateImageBounds);
      observer.disconnect();
    };
  }, [resultUrl, zoomLevel, panX, panY, currentSourceImage, sourceImage]);

  useEffect(() => {
    if (isOpen && generationInFlightRef.current) {
      setIsProcessing(true);
    }
  }, [isOpen]);

  // 重置状态（进行中时不清理 isProcessing，由请求的 finally 结束）
  useEffect(() => {
    if (!isOpen) {
      if (!generationInFlightRef.current) {
        setResultUrl(null);
        setSliderPosition(50);
        setProgressDesc('');
        setProgressPercent(0);
        setZoomLevel(1);
        setPanX(0);
        setPanY(0);
        setSelectedHistoryId(null);
      }
      setCurrentSourceImage(sourceImage ?? null);
    }
  }, [isOpen, sourceImage, userEmail, isAuthenticated]);

  // 鼠标滚轮缩放
  useEffect(() => {
    if (!resultUrl || !imageContainerRef.current) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      const newZoom = Math.max(0.5, Math.min(5, zoomLevel + delta));
      setZoomLevel(newZoom);
      
      // 如果缩放小于1，重置平移
      if (newZoom <= 1) {
        setPanX(0);
        setPanY(0);
      }
    };

    const container = imageContainerRef.current;
    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [resultUrl, zoomLevel]);

  // iPad：双指捏合缩放（与滚轮同一 zoomLevel）
  useEffect(() => {
    if (!resultUrl || !imageContainerRef.current) return;
    return attachComparePanelPinchZoom(imageContainerRef.current, {
      getZoom: () => zoomLevelPinchRef.current,
      setZoom: (z) => {
        zoomLevelPinchRef.current = z;
        setZoomLevel(z);
        if (z <= 1) {
          setPanX(0);
          setPanY(0);
        }
      },
      shouldIgnoreTouchTarget: (t) =>
        !!(t && typeof (t as HTMLElement).closest === 'function' && (t as HTMLElement).closest('.slider-handle')),
    });
  }, [resultUrl]);

  // 鼠标拖动平移（当缩放大于1时，且不在拖动滑杆时，且必须按住鼠标拖动）
  useEffect(() => {
    if (!resultUrl || !imageContainerRef.current || zoomLevel <= 1 || isDragging) return;

    const handleMouseDown = (e: MouseEvent) => {
      // 如果点击的是滑杆，不触发平移
      const target = e.target as HTMLElement;
      if (target.closest('.cursor-ew-resize') || target.closest('.slider-handle')) return;
      
      // 只有左键且按住时才触发平移
      if (e.button === 0) {
        setIsPanning(true);
        setPanStart({ x: e.clientX - panX, y: e.clientY - panY });
        e.preventDefault(); // 防止默认行为
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      // 只有在按住鼠标拖动时才平移，不是悬停时
      if (!isPanning || isDragging) return;
      
      const newPanX = e.clientX - panStart.x;
      const newPanY = e.clientY - panStart.y;
      
      // 动态计算平移范围，基于图片实际尺寸和缩放级别
      if (originalImageRef.current && imageContainerRef.current) {
        const imageRect = originalImageRef.current.getBoundingClientRect();
        const containerRect = imageContainerRef.current.getBoundingClientRect();
        
        // 计算图片缩放后的尺寸
        const scaledWidth = imageRect.width * zoomLevel;
        const scaledHeight = imageRect.height * zoomLevel;
        
        // 计算允许的最大平移范围（防止图片移出视口）
        // 当图片放大时，允许平移的范围 = (缩放后尺寸 - 容器尺寸) / 2
        const maxPanX = Math.max(0, (scaledWidth - containerRect.width) / 2 / zoomLevel);
        const maxPanY = Math.max(0, (scaledHeight - containerRect.height) / 2 / zoomLevel);
        
        setPanX(Math.max(-maxPanX, Math.min(maxPanX, newPanX)));
        setPanY(Math.max(-maxPanY, Math.min(maxPanY, newPanY)));
      } else {
        // 降级方案：如果无法获取尺寸，使用之前的固定值
      const maxPanX = (zoomLevel - 1) * 200;
      const maxPanY = (zoomLevel - 1) * 200;
      setPanX(Math.max(-maxPanX, Math.min(maxPanX, newPanX)));
      setPanY(Math.max(-maxPanY, Math.min(maxPanY, newPanY)));
      }
    };

    const handleMouseUp = () => {
      setIsPanning(false);
    };

    const container = imageContainerRef.current;
    container.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resultUrl, zoomLevel, isPanning, isDragging, panStart, panX, panY]);

  if (!isOpen) return null;

  const shellBg = isDarkMode ? 'bg-black text-white' : 'bg-zinc-50 text-zinc-900';
  const headerBg = isDarkMode ? 'bg-black/80 border-white/10' : 'bg-white border-zinc-200';
  const asideBg = isDarkMode ? 'bg-black/60 border-white/10' : 'bg-white border-zinc-200';
  const panelBg = isDarkMode ? 'bg-black' : 'bg-zinc-50';
  const cardBg = isDarkMode ? 'bg-black/20 border-white/10' : 'bg-white border-zinc-200';
  const textFaint = isDarkMode ? 'text-white/50' : 'text-zinc-500';
  const textMuted = isDarkMode ? 'text-white/60' : 'text-zinc-600';
  const btnGhost = isDarkMode ? 'bg-white/10 hover:bg-white/20 text-white' : 'bg-zinc-200 hover:bg-zinc-300 text-zinc-900';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={`fixed inset-0 z-[2000] ${shellBg}`}
        style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'San Francisco', 'Helvetica Neue', Helvetica, Arial, sans-serif" }}
        ref={containerRef}
      >
        {/* 顶部控制栏 */}
        <div
          className={`absolute top-0 left-0 right-0 z-[2100] h-16 border-b px-4 flex items-center justify-between ${headerBg} ${
            isDarkMode ? 'backdrop-blur-md' : ''
          }`}
        >
          <div className="flex items-center gap-4 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <h2 className="font-bold text-lg tracking-tight min-w-0 normal-case">
                {t('Upscale', '高清放大')}
              </h2>
              {runningHubLinked ? (
                <span
                  className="w-2 h-2 rounded-full bg-[#4ade80] shrink-0 shadow-[0_0_8px_rgba(74,222,128,0.65)]"
                  role="status"
                  title={
                    language === 'zh'
                      ? '后端已能连通 RunningHub（定期检测）'
                      : 'Backend can reach RunningHub (periodic check)'
                  }
                />
              ) : null}
            </div>
            {isProcessing && (
              <div className="flex items-center gap-2 text-[#D7CDA1] text-sm">
                <div className="w-2 h-2 bg-[#D7CDA1] rounded-full animate-pulse"></div>
                <span>{progressDesc}</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownload}
              disabled={!resultUrl}
              className="px-4 py-2 bg-[#D7CDA1] text-[#101820] font-bold text-xs tracking-wider uppercase hover:bg-[#D7CDA1] disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              style={{ borderRadius: '5px' }} // rounded-lg (8px) 减少20%再减少10% = 5.4px ≈ 5px
            >
              {t('Download', '下载')}
            </button>
            <button
              onClick={onClose}
              className={`p-2 transition-all ${btnGhost}`}
              style={{ borderRadius: '5px' }} // rounded-lg (8px) 减少20%再减少10% = 5.4px ≈ 5px
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* 左侧控制面板 */}
        <div
          className={`absolute left-0 top-16 bottom-0 w-80 p-6 overflow-y-auto no-scrollbar z-[2100] ${asideBg} ${
            isDarkMode ? 'backdrop-blur-md border-r border-white/10' : 'border-r border-zinc-200'
          }`}
        >
          <div className="space-y-6">
            {/* 输入源：点击或拖入本地图片；无主界面图时也可单独载入 */}
            <div>
              <h3 className={`text-[10px] font-semibold uppercase tracking-wider mb-3 ${textFaint}`}>
                {t('INPUT SOURCE', '输入源')}
              </h3>
              <input
                ref={sourceFileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) void applyLocalSourceFile(f);
                }}
              />
              <div
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    sourceFileInputRef.current?.click();
                  }
                }}
                className={`aspect-[3/4] overflow-hidden border flex flex-col items-center justify-center cursor-pointer transition-colors relative group ${
                  sourceDropOver
                    ? 'border-[#D7CDA1] border-2 ring-2 ring-[#D7CDA1]/40'
                    : ''
                } ${cardBg}`}
                style={{ borderRadius: '10px' }}
                onClick={() => sourceFileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onDragEnter={(e) => {
                  e.preventDefault();
                  setSourceDropOver(true);
                }}
                onDragLeave={(e) => {
                  const related = e.relatedTarget as Node | null;
                  if (related && (e.currentTarget as HTMLElement).contains(related)) return;
                  setSourceDropOver(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSourceDropOver(false);
                  const f = Array.from(e.dataTransfer.files).find((x) => x.type.startsWith('image/'));
                  if (f) void applyLocalSourceFile(f);
                }}
              >
                {effectiveSourceImage ? (
                  <img
                    src={effectiveSourceImage}
                    alt="Source"
                    className="w-full h-full object-contain pointer-events-none"
                    draggable={false}
                  />
                ) : (
                  <div className={`flex flex-col items-center justify-center gap-2 px-4 text-center ${textFaint}`}>
                    <span className="text-3xl leading-none text-white/30 group-hover:text-white/50">+</span>
                    <p className="text-[10px] leading-snug">
                      {t('Click or drag an image here', '点击或拖入图片')}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* 放大倍率选择 */}
            <div>
              <div className="flex justify-between items-center mb-3">
                <h3 className={`text-[10px] font-semibold uppercase tracking-wider ${textFaint}`}>
                  {t('UPSCALE VALUE', '放大倍率')}
                </h3>
                <span className="text-[#D7CDA1] font-semibold text-base">{getResolutionLabel(scale)}</span>
              </div>
              <input
                type="range"
                min="2"
                max="4"
                step="1"
                value={scale}
                onChange={(e) => setScale(parseInt(e.target.value))}
                className="w-full custom-range upscale-scale-range"
                disabled={isProcessing}
              />
              <div className={`flex justify-between text-[8px] font-semibold mt-2 ${textFaint}`}>
                <span>2</span>
                <span>3</span>
                <span>4</span>
              </div>
            </div>

            {/* 开始按钮 */}
            <button
              onClick={handleUpscale}
              disabled={isProcessing || !effectiveSourceImage}
              className="w-full py-5 bg-[#D7CDA1] text-[#101820] font-bold text-sm tracking-wider uppercase hover:bg-[#D7CDA1] active:scale-[0.97] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderRadius: '9px' }} // rounded-xl (12px) 减少20%再减少10% = 9px
            >
              {isProcessing 
                ? t('PROCESSING...', '正在处理...') 
                : t('START UPSCALE', '开始放大')}
            </button>

            {/* 说明文字 */}
            <div className={`text-[10px] leading-relaxed mt-4 font-normal ${textFaint}`}>
              <p>{t('Note: Generation may take several minutes. Please wait patiently.', '注意：生成可能需要几分钟时间，请耐心等待。')}</p>
              <p className="mt-2 text-[#D7CDA1]">
                {language === 'en'
                  ? 'Due to the algorithm, AI generation may not be satisfying on the first try. If you are not satisfied, click the button to generate again.'
                  : 'AI生成因算法原因，并非一次达到满意效果，如不满意，点击按钮继续生成。'}
              </p>
              <p className="mt-2 text-[#D7CDA1]">
                {language === 'en' 
                  ? '2: 4K Upscale • 3: 6K Upscale • 4: 8K Upscale • Higher resolution takes longer.'
                  : '2: 4K放大 • 3: 6K放大 • 4: 8K放大 • 分辨率越大时间越久。'}
              </p>
              <p className="mt-2 text-[#D7CDA1]">
                {language === 'en'
                  ? 'Friendly tip: due to image texture, higher resolution is not always better. If you are not satisfied, adjust the upscale value and run again.'
                  : '友情提示：因图片中纹理原因,并非分辨率越大效果越好，如不满意可调节放大按钮进行再次放大生成。'}
              </p>
              <p
                className={`mt-2 text-[10px] font-normal ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}
              >
                {language === 'en'
                  ? 'If the preview loads incorrectly, try switching to another image, then return here to check.'
                  : '图片加载异常时，请尝试切换其他图片后再返回查看。'}
              </p>
            </div>

            {/* 进度条显示 */}
            {isProcessing && (
              <div className="mt-6">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-[9px] text-white/60 font-semibold">{t('Progress', '进度')}</span>
                  <span className="text-[9px] text-[#D7CDA1] font-semibold">{Math.round(progressPercent)}%</span>
                </div>
                <div className="w-full h-2 bg-black/40 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-[#D7CDA1] transition-all duration-300 ease-out"
                    style={{ width: `${progressPercent}%` }}
                  />
                </div>
                <p className="text-[8px] text-white/50 mt-1 font-normal">{progressDesc}</p>
              </div>
            )}

            {/* 历史记录区域 */}
            <div className="mt-6 pt-6 border-t border-white/10">
              <h3 className="text-[10px] font-semibold uppercase tracking-wider text-white/50 mb-3">
                {t('UPSCALE HISTORY', '放大历史')}
              </h3>
              {upscaleHistory.length === 0 ? (
                <div className="text-[10px] text-white/50 text-center py-4 font-normal">
                  {t('No upscale history yet', '暂无放大历史')}
                </div>
              ) : (
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {upscaleHistory.map((item) => {
                    const isSelected = selectedHistoryId === item.id;
                    return (
                      <div
                        key={item.id}
                        onClick={() => handleSelectHistoryItem(item)}
                        className={`overflow-hidden border bg-black/20 hover:border-[#D7CDA1]/50 cursor-pointer transition-all group relative ${
                          isSelected ? 'border-[#D7CDA1] border-2' : 'border-white/10'
                        }`}
                        style={{ borderRadius: '9px' }} // rounded-xl (12px) 减少20%再减少10% = 9px
                      >
                      <div className="aspect-video relative bg-black/40">
                        <RunningHubHistoryListThumb
                          item={item}
                          imgClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute top-2 left-2 z-[14]">
                          <RunningHubCosExpiryBadge timestamp={item.timestamp} lang={language === 'en' ? 'en' : 'zh'} />
                        </div>
                        <div className="absolute top-2 right-2 bg-[#D7CDA1] text-[#101820] text-[8px] font-semibold px-2 py-1 rounded">
                          {getResolutionLabel(item.scale)} {t('UPSCALED', '放大')}
                        </div>
                        {/* 水印 */}
                        <div className="absolute bottom-1 right-1 z-[15] pointer-events-none">
                          <Watermark size="small" />
                        </div>
                        {/* 下载和删除按钮 */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center gap-2 p-2">
                          <button
                            onClick={(e) => handleDownloadHistoryItem(e, item)}
                            className="px-3 py-1.5 bg-[#D7CDA1] text-[#101820] text-[8px] font-semibold uppercase hover:bg-[#D7CDA1] transition-all"
                            style={{ borderRadius: '4px' }}
                            title={t('Download', '下载')}
                          >
                            {t('Download', '下载')}
                          </button>
                          <button
                            onClick={(e) => handleDeleteHistoryItem(e, item.id)}
                            className="px-3 py-1.5 bg-red-600 text-white text-[8px] font-semibold uppercase hover:brightness-110 transition-all"
                            style={{ borderRadius: '4px' }}
                            title={t('Delete', '删除')}
                          >
                            {t('Delete', '删除')}
                          </button>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 主对比区域 */}
        <div className={`absolute left-80 right-0 top-16 bottom-0 z-[2000] ${panelBg}`}>
          {resultUrl ? (
            /* 滑杆对比视图 - 汽车贴膜效果 */
            <div 
              ref={imageContainerRef}
              className={`relative w-full h-full overflow-hidden touch-none ${panelBg}`}
              style={{ cursor: zoomLevel > 1 && isPanning ? 'grabbing' : zoomLevel > 1 ? 'grab' : 'default' }}
            >
              {/* 统一的图片容器 - 确保两张图片完全对齐，一起缩放；touch-none 减轻 iPad 与滑杆竞争滚动 */}
              <div 
                className="absolute inset-0 flex items-center justify-center touch-none"
                style={{
                  transform: `scale(${zoomLevel}) translate(${panX / zoomLevel}px, ${panY / zoomLevel}px)`,
                  transformOrigin: 'center center',
                  transition: isDragging || isPanning ? 'none' : 'transform 0.1s ease-out',
                  width: '100%',
                  height: '100%',
                  overflow: 'visible'
                }}
              >
                {/* 统一的图片尺寸容器 - 两张图片完全重叠，四个角完全对齐 */}
                <div 
                  ref={imageWrapperRef}
                  className="relative"
                  style={{ 
                    display: 'inline-block',
                    maxWidth: '100%',
                    maxHeight: '100vh'
                  }}
                >
                  {/* 底层：原始图（完整显示，作为背景，容器大小由它决定） */}
                  <img 
                    ref={originalImageRef}
                    src={effectiveSourceImage || ''} 
                    alt="Original" 
                    className="select-none pointer-events-none"
                    style={{
                      display: 'block',
                      maxWidth: '100%',
                      maxHeight: 'calc(100vh - 64px)', // 减去顶部控制栏高度，确保图片完整显示
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain',
                      verticalAlign: 'top' // 确保顶部对齐
                    }}
                    draggable={false}
                    onLoad={() => {
                      // 当原始图加载完成后，同步放大图尺寸和位置，确保四个角完全对齐
                      if (originalImageRef.current && upscaledImageRef.current && imageContainerRef.current) {
                        const original = originalImageRef.current;
                        const upscaled = upscaledImageRef.current;
                        
                        // 获取原始图的实际显示尺寸（像素级精确）
                        const originalDisplayWidth = original.offsetWidth;
                        const originalDisplayHeight = original.offsetHeight;
                        
                        // 设置放大图的尺寸，使其与原始图完全一致（四个角对齐）
                        upscaled.style.width = `${originalDisplayWidth}px`;
                        upscaled.style.height = `${originalDisplayHeight}px`;
                        upscaled.style.maxWidth = 'none'; // 移除 maxWidth 限制
                        upscaled.style.maxHeight = 'none'; // 移除 maxHeight 限制
                        
                        // 确保放大图完全覆盖原图，四个角完全对齐
                        upscaled.style.top = '0px';
                        upscaled.style.left = '0px';
                        upscaled.style.objectFit = 'contain'; // 与原始图保持一致
                        
                        // 更新图片实际显示区域（用于滑杆定位）
                        requestAnimationFrame(() => {
                          if (originalImageRef.current && imageContainerRef.current) {
                            const imageRect = originalImageRef.current.getBoundingClientRect();
                            const containerRect = imageContainerRef.current.getBoundingClientRect();
                            setImageBounds({
                              left: imageRect.left - containerRect.left,
                              width: imageRect.width
                            });
                          }
                        });
                      }
                    }}
                  />

                  {/* 上层：放大图（完全重叠在原始图上，四个角完全对齐，通过 clipPath 显示右侧部分） */}
                  <img 
                    ref={upscaledImageRef}
                    src={resultUrl} 
                    alt="Upscaled" 
                    className="absolute select-none pointer-events-none"
                    style={{
                      position: 'absolute',
                      top: '0px',
                      left: '0px',
                      width: 'auto',
                      height: 'auto',
                      objectFit: 'contain',
                      clipPath: `inset(0 0 0 ${sliderPosition}%)`,
                      transition: isDragging ? 'none' : 'clip-path 0.05s linear',
                      display: 'block',
                      margin: '0',
                      padding: '0',
                      border: 'none',
                      outline: 'none'
                    }}
                    draggable={false}
                    onLoad={() => {
                      // 当放大图加载完成后，同步尺寸和位置，确保四个角完全对齐
                      if (originalImageRef.current && upscaledImageRef.current) {
                        const original = originalImageRef.current;
                        const upscaled = upscaledImageRef.current;
                        
                        // 获取原始图的实际显示尺寸（像素级精确）
                        const originalDisplayWidth = original.offsetWidth;
                        const originalDisplayHeight = original.offsetHeight;
                        
                        // 设置放大图的尺寸，使其与原始图完全一致（四个角对齐）
                        upscaled.style.width = `${originalDisplayWidth}px`;
                        upscaled.style.height = `${originalDisplayHeight}px`;
                        upscaled.style.maxWidth = 'none'; // 移除 maxWidth 限制
                        upscaled.style.maxHeight = 'none'; // 移除 maxHeight 限制
                        
                        // 确保放大图完全覆盖原图，四个角完全对齐
                        upscaled.style.top = '0px';
                        upscaled.style.left = '0px';
                        upscaled.style.objectFit = 'contain'; // 与原始图保持一致
                      }
                    }}
                  />
                  {/* 水印 - 放大图 */}
                  <div className="absolute bottom-2 right-2 z-[15] pointer-events-none">
                    <Watermark size="small" />
                  </div>
                </div>
              </div>

              {/* 滑杆：Pointer Events + 加宽命中区（iPad / Pencil） */}
              <div
                className="absolute top-0 bottom-0 z-[2200] flex justify-center cursor-ew-resize touch-none slider-handle"
                style={{
                  left: imageBounds ? `${imageBounds.left + (sliderPosition / 100) * imageBounds.width}px` : '50%',
                  width: 44,
                  marginLeft: -22,
                  boxShadow: 'none',
                  transition: isDragging ? 'none' : 'left 0.05s linear',
                }}
                onPointerDown={(e) =>
                  startCompareSliderPointerDrag(e, {
                    originalImageRef,
                    imageContainerRef,
                    setSliderPercent: setSliderPosition,
                    setDragging: setIsDragging,
                    setImageBounds,
                  })
                }
                onTouchStart={(e) =>
                  startCompareSliderTouchDrag(e, {
                    originalImageRef,
                    imageContainerRef,
                    setSliderPercent: setSliderPosition,
                    setDragging: setIsDragging,
                    setImageBounds,
                  })
                }
              >
                <div className="w-0.5 h-full shrink-0 bg-white/90 self-center pointer-events-none" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center border-2 border-black pointer-events-none">
                  <svg className="w-4 h-4 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                  </svg>
                </div>
              </div>

              {/* 标签 */}
              <div className={`absolute top-4 left-4 backdrop-blur-md px-3 py-1.5 border ${isDarkMode ? 'bg-black/60 border-white/10' : 'bg-white/70 border-zinc-200'}`} style={{ borderRadius: '6px' }}> {/* rounded-lg (8px) 减少20% = 6.4px ≈ 6px */}
                <span className={`text-[10px] font-semibold tracking-wider ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                  {t('ORIGINAL', '原始')}
                </span>
              </div>
              <div className={`absolute top-4 right-4 backdrop-blur-md px-3 py-1.5 border ${isDarkMode ? 'bg-black/60 border-white/10' : 'bg-white/70 border-zinc-200'}`} style={{ borderRadius: '6px' }}> {/* rounded-lg (8px) 减少20% = 6.4px ≈ 6px */}
                <span className="text-[10px] font-semibold text-[#D7CDA1] tracking-wider">
                  {getResolutionLabel(scale)} {t('UPSCALED', '放大')}
                </span>
              </div>
            </div>
          ) : (
            /* 预览区域（未处理时） */
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-center">
                <div className={`w-24 h-24 mx-auto mb-4 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-white/5' : 'bg-zinc-100'}`}>
                  <svg className={`w-12 h-12 ${isDarkMode ? 'text-white/20' : 'text-zinc-300'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <p className={`${textFaint} text-sm font-semibold tracking-wider`}>
                  {t('PREVIEW AREA', '预览区域')}
                </p>
                <p className={`${isDarkMode ? 'text-white/40' : 'text-zinc-500'} text-xs mt-2 font-normal`}>
                  {t('Click "START UPSCALE" to begin', '点击"开始放大"开始处理')}
                </p>
              </div>
            </div>
          )}
        </div>

      </motion.div>
    </AnimatePresence>
  );
};

export default UpscalePanel;

